import { useEffect, useState } from "react";
import { format } from "date-fns";
import { BsCheckCircle, BsClock, BsSearch, BsPencilSquare, BsXCircle, BsUpload } from "react-icons/bs";
import api from "../../api";
import Input from "../../components/form/Input";
import Textarea from "../../components/form/Textarea";

export default function ReportsSection() {
  const [reports, setReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [expandedReportId, setExpandedReportId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topic, setTopic] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingReport, setEditingReport] = useState(null);

  const fetchReports = async () => {
    try {
      const { data } = await api.get(`${process.env.REACT_APP_API_BASE_URL}report`);
      setReports(data);
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const reportCounts = {
    all: reports.length,
    reviewed: reports.filter((r) => r.status === "approved").length,
    pending: reports.filter((r) => r.status === "pending").length,
    rejected: reports.filter((r) => r.status === "rejected").length,
  };

  const filteredReports = reports.filter((report) => {
    const matchesSearch =
      report.topic.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesTab =
      activeTab === "all" ||
      (activeTab === "reviewed" && report.status === "approved") ||
      (activeTab === "pending" && report.status === "pending") ||
      (activeTab === "rejected" && report.status === "rejected");

    return matchesSearch && matchesTab;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!topic.trim() || !description.trim()) return;

    setIsSubmitting(true);
    setIsSuccess(false);

    try {
      if (editMode) {
        await api.put(
          `${process.env.REACT_APP_API_BASE_URL}report/edit/${editingReport._id}`,
          { topic, description }
        );
      } else {
        await api.post(`${process.env.REACT_APP_API_BASE_URL}report`, { topic, description });
      }

      setTopic("");
      setDescription("");
      setIsSuccess(true);
      setEditMode(false);
      setEditingReport(null);
      fetchReports();
      setTimeout(() => setIsSuccess(false), 1000);
    } catch (error) {
      if (
        error.response &&
        error.response.status === 400 &&
        error.response.data.message === "Report for today already submitted"
      ) {
        window.alert("You have already submitted a report for today.");
      } else {
        console.error("Submission failed:", error);
        window.alert("Failed to submit the report. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleEditClick = (report) => {
    setEditingReport(report);
    setTopic(report.topic);
    setDescription(report.description);
    setEditMode(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelEdit = () => {
    setTopic("");
    setDescription("");
    setEditMode(false);
    setEditingReport(null);
  };

  return (
    <div className="w-full p-4 md:p-6  shadow-md rounded-lg space-y-10  grid gap-6 grid-cols-1 md:grid-cols-2">
      {/* Submit / Edit Form */}
      {/* Daily Report Form */}
      <div className="bg-white shadow-md rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-2">
          {editMode ? "Edit Daily Report" : "Submit Daily Report"}
        </h2>
        <p className="text-gray-600 mb-4">
          {editMode
            ? "Update your report below."
            : `Submit your daily progress for ${format(new Date(), "MMMM d, yyyy")}`}
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            id="title"
            name="title"
            placeholder="Enter title"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            required
          />

          <Textarea
            id="report"
            label="Daily Progress"
            placeholder="Describe what you've learned and accomplished today..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
          />

          <div className="flex flex-col sm:flex-row flex-wrap justify-between gap-4">
            {editMode && (
              <button
                type="button"
                onClick={handleCancelEdit}
                className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 w-full sm:w-auto"
              >
                <BsXCircle className="text-xl" />
                Cancel
              </button>
            )}

            <button
              type="button"
              onClick={() => {
                setTopic("");
                setDescription("");
              }}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-500 w-full sm:w-auto"
            >
              Clear Form
            </button>

            <button
              type="submit"
              disabled={isSubmitting || isSuccess}
              className={`flex items-center justify-center gap-2 px-4 py-2 text-white rounded w-full sm:w-auto ${isSubmitting || isSuccess
                  ? "bg-green-500"
                  : "bg-blue-600 hover:bg-blue-700"
                }`}
            >
              {isSubmitting ? (
                <>
                  <BsUpload className="animate-spin" />
                  Submitting...
                </>
              ) : isSuccess ? (
                <>
                  <BsCheckCircle />
                  Submitted!
                </>
              ) : (
                <>
                  <BsUpload />
                  {editMode ? "Update Report" : "Submit Report"}
                </>
              )}
            </button>
          </div>

        </form>
      </div>


      {/* Search and Tabs */}
     <div>
     <div>
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="relative flex-1">
            <BsSearch className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search reports..."
              className="w-full p-2 pl-10 border rounded-md text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-4">
          {["all", "reviewed", "pending", "rejected"].map((tab) => (
            <button
              key={tab}
              className={`px-3 py-2 text-xs md:text-sm rounded ${activeTab === tab
                  ? tab === "reviewed"
                    ? "bg-green-500 text-white"
                    : tab === "pending"
                      ? "bg-yellow-500 text-white"
                      : tab === "rejected"
                        ? "bg-red-500 text-white"
                        : "bg-blue-500 text-white"
                  : "bg-gray-200 text-gray-700"
                }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)} ({reportCounts[tab]})
            </button>
          ))}
        </div>
      </div>

      {/* Reports Listing */}
      <div>
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, index) => (
              <div
                key={index}
                className="p-4 border rounded-md bg-gray-200 h-20 animate-pulse relative overflow-hidden"
              ></div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredReports.length > 0 ? (
              filteredReports.map((report) => (
                <div key={report._id} className="border rounded-md overflow-hidden">
                  <div
                    className="p-4 cursor-pointer hover:bg-gray-100"
                    onClick={() =>
                      setExpandedReportId(expandedReportId === report._id ? null : report._id)
                    }
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-sm md:text-base">{report.topic}</h3>
                        <p className="text-xs text-gray-500">
                          {format(new Date(report.date), "MMMM d, yyyy")}
                        </p>
                      </div>
                      <span
                        className={`text-xs px-2 py-1 rounded ${report.status === "approved"
                            ? "bg-green-200 text-green-800"
                            : report.status === "rejected"
                              ? "bg-red-200 text-red-800"
                              : "bg-yellow-200 text-yellow-800"
                          }`}
                      >
                        {report.status === "approved" ? (
                          <BsCheckCircle className="inline-block mr-1" />
                        ) : (
                          <BsClock className="inline-block mr-1" />
                        )}
                        {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                      </span>
                    </div>
                    <p className="mt-2 text-xs md:text-sm text-gray-700">{report.description}</p>
                  </div>

                  {/* Rejected Feedback + Edit */}
                  {expandedReportId === report._id && report.status === "rejected" && (
                    <div className="p-4 bg-gray-50">
                      <p className="text-xs md:text-sm font-medium">Teacher Feedback:</p>
                      <p className="text-xs md:text-sm text-gray-700">
                        {report.rejectedReason || "No reason provided."}
                      </p>
                      <button
                        className="mt-2 px-3 py-2 text-xs md:text-sm bg-red-500 text-white rounded-md hover:bg-red-600"
                        onClick={() => handleEditClick(report)}
                      >
                        <BsPencilSquare className="inline-block mr-1" /> Make Changes
                      </button>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-center py-10 text-gray-500">
                No reports found matching your search criteria.
              </p>
            )}
          </div>
        )}
      </div>  
     </div>

    </div>
  );
}
